import { useState, useEffect } from 'react';
import { useLocation, useParams, useHistory } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faSquarePollHorizontal,
  faCalendarRange,
  faAngleLeft,
  faChain,
} from '@fortawesome/pro-regular-svg-icons';
import {
  BoxV2 as Box,
  Flex,
  Table,
  useTable,
  CampaignAttributes,
} from 'portal-commons';

import {
  RcsSectionBlock,
  CampaignDetailsSection,
  LaunchDetailsSection,
  TechnicalDetailsSection,
  SampleMessagesSection,
  CspDetailsSection,
  BrandDetailsSection,
  CampaignSummary,
  CarrierStatusSection,
} from '../components/RcsCampaignDetail';
import { Loader } from '@/shared_elements';
import {
  RCS_CSP_CAMPAIGNS_DEFAULT_ENTRY,
  RCS_DCA_CAMPAIGNS_DEFAULT_ENTRY,
  RCS_CSP_CAMPAIGNS_PATH,
  RCS_DCA_CAMPAIGNS_PATH,
} from '@/constants/paths';
import EventListingRow from '../components/EventsListingRow';
import SharingChain from '../components/SharingChain';
import { MAX_EVENT_RECORDS } from '../../events/constants';
import {
  campaignController,
  enumController,
  eventHistoryController,
} from '@/apis/core';
import {
  CampaignSharingChain,
  CampaignDetail,
  VerticalType,
} from '@/apis/core/types';
import { useCampaignAsset } from '@/hooks';
import { useAuthContext } from '@/contexts';

const EVENT_TABLE_HEADERS = [
  { id: 'eventType', label: 'Event Type', sortable: false },
  { id: 'categoryType', label: 'Category', sortable: false },
  { id: 'campaignUid', label: 'Campaign ID', sortable: false },
  { id: 'brandUid', label: 'Brand ID', sortable: false },
  { id: 'updateDate', label: 'Date & Time', sortable: false },
  { id: 'description', label: 'Description', sortable: false },
];

const RcsCampaignDetail = () => {
  const location = useLocation<{ goBackPage?: string }>();
  const history = useHistory();
  const [pageLoading, setPageLoading] = useState(true);
  const { id: campaignUid } = useParams<{ id: string }>();
  const [goBackPage] = useState(location.state?.goBackPage || '');
  const [campaign, setCampaign] = useState<CampaignDetail>();
  const [sharingChainNodes, setSharingChainNodes] = useState<
    CampaignSharingChain['nodes']
  >([]);
  const { profile } = useAuthContext();
  const [verticalTypes, setVerticalTypes] = useState<VerticalType[]>([]);

  const { logo, banner } = useCampaignAsset(campaign);

  const eventTable = useTable<Event>(
    async (tableFilter) => {
      const response = await eventHistoryController.getEventHistories({
        campaignUid,
        recordsPerPage: 10,
        page: tableFilter?.page as number,
      });
      return {
        data: response?.records || [],
        total: response?.totalRecords || 0,
        rowsPerPage: response?.recordsPerPage || 10,
        page: response?.page || 1,
      };
    },
    {
      deps: [campaignUid],
      inMemory: true,
      cappingTotal: MAX_EVENT_RECORDS,
    }
  );

  const getCampaignDetail = async (uid: string, silent = false) => {
    !silent && setPageLoading(true);
    const newCampaign = await campaignController.getCampaignDetail(uid, {});
    setCampaign(newCampaign);
    !silent && setPageLoading(false);
  };

  const getSharingChain = async () => {
    if (campaignUid) {
      const data = await campaignController.getSharingChain(campaignUid);
      setSharingChainNodes(data?.nodes || []);
    }
  };

  const getVerticalTypes = async () => {
    const response = await enumController.getVerticalTypes();
    setVerticalTypes(response);
  };

  useEffect(() => {
    getCampaignDetail(campaignUid);
    getSharingChain();
    getVerticalTypes();

    return () => {
      setCampaign(undefined);
      setSharingChainNodes([]);
    };
  }, [campaignUid]);

  const goBackLink: { text: string; path: string } = (() => {
    const linkEntries = [
      { prefix: '/dashboard', text: 'back to dashboard' },
      { prefix: RCS_CSP_CAMPAIGNS_PATH, text: 'back to campaigns' },
      { prefix: RCS_DCA_CAMPAIGNS_PATH, text: 'back to campaigns' },
    ];
    const matchedEntry = linkEntries.find(
      (e) => goBackPage && goBackPage.startsWith(e.prefix)
    );

    if (goBackPage && matchedEntry) {
      return { text: matchedEntry.text, path: goBackPage };
    }

    const path = RCS_DCA_CAMPAIGNS_PATH.includes(location.pathname)
      ? RCS_DCA_CAMPAIGNS_DEFAULT_ENTRY
      : RCS_CSP_CAMPAIGNS_DEFAULT_ENTRY;

    return {
      text: 'back',
      path,
    };
  })();

  if (pageLoading) return <Loader />;

  return (
    <Box>
      <Flex
        as="a"
        sx={{ mb: 'xl', width: 'fit-content', cursor: 'pointer' }}
        onClick={() => history.push(goBackLink.path)}
        data-testid="goBackBtn"
      >
        <Box sx={{ mr: 'xs' }}>
          <FontAwesomeIcon icon={faAngleLeft} color="#C4C8C9" />
        </Box>
        {goBackLink.text}
      </Flex>
      <CampaignSummary
        campaign={campaign}
        currentStatus={{
          rcsVet: campaign?.brand?.rcsVettingStatus === 'ACTIVE',
          logo: campaign?.brand?.logoStatus === 'VERIFIED',
          banner: campaign?.brand?.bannerStatus === 'VERIFIED',
        }}
      />
      <Flex
        sx={{
          flexDirection: 'column',
          gap: 'xl',
        }}
      >
        <CarrierStatusSection
          campaign={campaign}
          onAcceptElection={() => {
            // to update Google Agent ID
            getCampaignDetail(campaignUid, true);
          }}
          onDeclineElection={() => history.push(goBackLink.path)}
        />
        <RcsSectionBlock
          icon={faChain}
          title="Connectivity Chain"
          showDivider={true}
        >
          <SharingChain nodes={sharingChainNodes} />
        </RcsSectionBlock>
        <BrandDetailsSection
          campaign={campaign}
          verticalTypes={verticalTypes}
        />
        <CspDetailsSection campaign={campaign} />
        <CampaignDetailsSection
          campaign={campaign}
          logo={{
            name: logo?.name,
            type: 'LOGO',
            verificationStatus: logo?.verificationStatus,
            url: logo?.url,
          }}
          banner={{
            name: banner?.name,
            type: 'BANNER',
            verificationStatus: banner?.verificationStatus,
            url: banner?.url,
          }}
        />
        <LaunchDetailsSection campaign={campaign} />
        <TechnicalDetailsSection
          campaign={campaign}
          isRedacted={profile?.dpaSigned !== true}
        />
        <SampleMessagesSection
          messages={[
            campaign?.sample1,
            campaign?.sample2,
            campaign?.sample3,
            campaign?.sample4,
            campaign?.sample5,
          ].filter((s) => s != null)}
        />
        <RcsSectionBlock
          icon={faSquarePollHorizontal}
          title="Compliance Attributes"
          showDivider={true}
        >
          <CampaignAttributes.ReadonlyPanel
            type="rcs"
            data={{
              ...campaign,
              optinKeywords: campaign?.optinKeywords?.split(',') || [],
              optoutKeywords: campaign?.optoutKeywords?.split(',') || [],
              helpKeywords: campaign?.helpKeywords?.split(',') || [],
            }}
          />
        </RcsSectionBlock>
        <RcsSectionBlock
          icon={faCalendarRange}
          title="Events"
          showDivider={true}
        >
          <Box sx={{ 'tr td': { padding: '0 10px 0 16px' } }}>
            <Table
              testId="campaignDetailEventsTable"
              className="campaign-detail-events-table"
              headRows={EVENT_TABLE_HEADERS}
              emptyState="no events to view"
              disableHover
              loading={eventTable.loading}
              tableData={eventTable.tableData.map((record, index) => (
                <EventListingRow
                  key={index}
                  event={record}
                  brandLink
                  withoutUser
                />
              ))}
              handleChangePage={eventTable.handleChangePage}
              pagination={eventTable.pagination}
              headerTextTransform="none"
              emptyBlockHeight="180px"
            />
          </Box>
        </RcsSectionBlock>
      </Flex>
    </Box>
  );
};

export default RcsCampaignDetail;
